import type { Specialty } from "./branding";
import { z } from "zod";
import { email, text, uuid } from "./common";
import type { Permission } from "./permissions";

/** Password policy: length over complexity (NIST SP 800-63B). The API also rejects common passwords. */
export const password = z.string().min(10, "Use at least 10 characters").max(128);

export const loginInput = z.object({ email, password: z.string().min(1).max(128) });
export const forgotPasswordInput = z.object({ email });
export const resetPasswordInput = z.object({ token: z.string().min(20).max(200), password });
export const acceptInvitationInput = z.object({
  token: z.string().min(20).max(200),
  password,
  fullName: text(200).optional(),
});
export const switchTenantInput = z.object({ tenantId: uuid });
export const changePasswordInput = z.object({ currentPassword: z.string().min(1).max(128), newPassword: password });

export interface TenantSummary {
  id: string;
  name: string;
  slug: string;
  status: "trial" | "active" | "suspended" | "cancelled";
}

/** What the web app needs after login / on page load (GET /api/auth/session). */
export interface SessionView {
  user: { id: string; email: string; fullName: string; mfaEnabled: boolean };
  isPlatformAdmin: boolean;
  tenants: TenantSummary[];
  activeTenant: (TenantSummary & { timeZone: string; currencyCode: string; locale: string; countryCode: string; trialEndsAt: string | null; specialty: Specialty }) | null;
  /** Effective permissions in the active tenant (union of roles). Used only to shape the UI; the API re-checks. */
  permissions: Permission[];
  /** null = every branch; otherwise the branches this user's roles are limited to. */
  branchIds: string[] | null;
  features: Record<string, { enabled: boolean; limit: number | null }>;
  /** The signed-in user's doctor record in the active clinic (null if they are not a doctor there). */
  doctorId: string | null;
}

/* ---------------------------------------------------------------- two-step sign-in (Phase 8a) */

/** A 6-digit authenticator code or a recovery code ("k7m2q-x9p4t"). */
export const mfaCode = z.string().trim().min(6).max(20);
export const mfaVerifyInput = z.object({ code: mfaCode });
export const mfaSetupInput = z.object({ password: z.string().min(1).max(128) });
export const mfaConfirmInput = z.object({ code: mfaCode });
export const mfaDisableInput = z.object({ password: z.string().min(1).max(128), code: mfaCode });
export const mfaRecoveryInput = z.object({ password: z.string().min(1).max(128), code: mfaCode });

/** Login answer when the account has two-step sign-in: the cookie is set but only works after /api/auth/mfa/verify. */
export interface MfaChallenge {
  mfaRequired: true;
}
export type LoginResult = SessionView | MfaChallenge;
export const isMfaChallenge = (r: LoginResult): r is MfaChallenge => "mfaRequired" in r && r.mfaRequired === true;

export interface MfaStatus {
  enabled: boolean;
  enrolledAt: string | null;
  lastUsedAt: string | null;
  recoveryCodesLeft: number;
}
export interface MfaSetup {
  /** Base32 secret to type into the authenticator app if the QR code cannot be scanned. */
  secret: string;
  otpauthUrl: string;
}

export interface ActiveSession {
  id: string;
  current: boolean;
  ip: string | null;
  userAgent: string | null;
  createdAt: string;
  lastSeenAt: string;
}

/** Platform support: switch off a user's two-step sign-in after checking who they are (lost phone and codes). */
export const platformMfaResetInput = z.object({ email, reason: text(500) });
/** Support: correct a user's name (e.g. a title typed by mistake). */
export const platformUserNameInput = z.object({ email, fullName: text(200) });
